import { useEffect, useRef, useState } from 'react';
import { serializeSummaryLines, summaryLines, summaryReviewCopy } from '../utils/siteBriefSummaryReview';

function PointText({ text }: { text: string }) {
  const label = /^([^:\n]{1,60}:)(\s+[\s\S]*)$/.exec(text);
  return label ? <><strong>{label[1]}</strong>{label[2]}</> : <>{text}</>;
}

export default function SiteBriefSummaryEditor({ summary, language, dirty, disabled, onChange, onSave, onDiscard }: {
  summary: string; language: string; dirty: boolean; disabled: boolean;
  onChange: (summary: string) => void; onSave: () => void; onDiscard?: () => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const copy = summaryReviewCopy(language);
  const [lines, setLines] = useState(() => summaryLines(summary));
  const source = useRef(summary);
  useEffect(() => {
    if (summary !== source.current) {
      source.current = summary;
      setLines(summaryLines(summary));
      setEditing(null);
    }
  }, [summary]);
  const update = (next: typeof lines) => {
    const text = serializeSummaryLines(next);
    source.current = text;
    setLines(next);
    onChange(text);
  };
  const change = (index: number, text: string) => update(lines.map((line, i) => i === index ? { ...line, text } : line));
  const remove = (index: number) => {
    update(lines.filter((_, i) => i !== index));
    setEditing(null);
  };
  const add = (kind: 'point' | 'heading') => {
    update([...lines, { kind, text: kind === 'point' ? copy.newPoint : copy.newSection }]);
    setEditing(lines.length);
  };
  return (
    <section className="sb-summary-editor" lang={language} aria-label={copy.title}>
      <p className="sb-summary-editor-hint">{copy.hint}</p>
      <div className="sb-brief-summary">
        {lines.map((line, index) => (
          <div key={index} className={`sb-summary-line sb-summary-line-${line.kind}`}>
            {editing === index ? (
              <textarea
                autoFocus
                aria-label={`${copy.edit}: ${line.text}`}
                value={line.text}
                rows={Math.max(2, Math.ceil(line.text.length / 60))}
                disabled={disabled}
                onChange={(event) => change(index, event.target.value)}
                onBlur={() => setEditing(null)}
              />
            ) : (
              <button type="button" className="sb-summary-edit-text" disabled={disabled} onClick={() => setEditing(index)}>
                {line.kind === 'point' ? <><span aria-hidden="true">• </span><PointText text={line.text} /></> : line.text}
              </button>
            )}
            <button type="button" className="sb-summary-remove" aria-label={`${copy.remove}: ${line.text}`} disabled={disabled}
              onClick={() => remove(index)}>×</button>
          </div>
        ))}
      </div>
      <div className="sb-summary-edit-tools">
        <button type="button" disabled={disabled} onClick={() => add('point')}>+ {copy.add}</button>
        <button type="button" disabled={disabled} onClick={() => add('heading')}>+ {copy.addSection}</button>
      </div>
      <div className="sb-summary-save-row">
        <span role="status">{dirty ? copy.unsaved : copy.saved}</span>
        {dirty && onDiscard && <button type="button" className="sb-text-button" disabled={disabled} onClick={onDiscard}>{copy.discard}</button>}
        {dirty && <button type="button" className="sb-button sb-button-secondary" disabled={disabled || !summary.trim()} onClick={onSave}>{copy.save}</button>}
      </div>
    </section>
  );
}
